import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { RiDashboardLine, RiLogoutBoxRLine, RiSettings4Line } from "react-icons/ri";
import { FaBook } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import StudentWrapper from "../Auth/StudentWrapper";
import { logoutUserAction } from "../../modules/auth/Actions";

const C = {
  sidebar: "#7A1422",
  sidebarActive: "#A32434",
  cream: "#F7F2EC",
  footer: "#8B1828",
  border: "rgba(214, 206, 192, 0.7)",
};

const navItem = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 3,
  px: 4,
  py: 3,
  borderRadius: "md",
  fontWeight: active ? "800" : "600",
  fontSize: "sm",
  color: active ? "white" : "rgba(250, 242, 235, 0.85)",
  bg: active ? C.sidebarActive : "transparent",
  _hover: {
    bg: C.sidebarActive,
    color: "white",
    textDecoration: "none",
  },
});

/** Matches portal chrome — avoids default Chakra white / blue menu look */
const accountMenuListProps = {
  bg: C.footer,
  borderWidth: "1px",
  borderColor: C.border,
  borderRadius: "md",
  py: 1,
  minW: "10rem",
  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.35)",
  zIndex: 50,
};

const accountMenuItemProps = {
  bg: "transparent",
  color: C.cream,
  fontWeight: "600",
  fontSize: "sm",
  py: 2.5,
  px: 3,
  iconSpacing: 3,
  _hover: { bg: C.sidebarActive, color: "white" },
  _focus: { bg: C.sidebarActive, color: "white" },
  _active: { bg: C.sidebarActive, color: "white" },
};

/**
 * Student area: desktop fixed sidebar (md+); mobile compact top bar + full-width content below.
 */
const StudentPortalShell = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userData = useSelector((state) => state.user.userData);

  const isHome = pathname === "/students" || pathname === "/home";
  const isCourses = pathname.startsWith("/students/courses");

  const handleLogout = () => {
    dispatch(logoutUserAction());
  };

  const SidebarNav = (
    <>
      <Box px={8} pt={8} pb={4}>
        <Text fontSize="xl" fontWeight="800" color="white" letterSpacing="tight">
          Edutopia
        </Text>
        <Text fontSize="0.6875rem" fontWeight="800" letterSpacing="0.2em" color="rgba(250,242,235,0.8)" mt={1}>
          ACADEMIC PORTAL
        </Text>
      </Box>

      <VStack as="nav" align="stretch" spacing={2} px={4} flex="1" pt={2}>
        <Box as={Link} to="/students" {...navItem(isHome)}>
          <RiDashboardLine size={22} />
          <Text as="span">Home</Text>
        </Box>
        <Box as={Link} to="/students/courses" {...navItem(isCourses)}>
          <FaBook size={20} />
          <Text as="span">Courses</Text>
        </Box>
      </VStack>

      <Box mt="auto" borderTopWidth="1px" borderColor={C.border} bg={C.footer} p={4}>
        <Flex align="center" justify="space-between" gap={3}>
          <Text fontSize="sm" fontWeight="700" color={C.cream} noOfLines={1} flex="1">
            {userData?.name || "Student"}
          </Text>
          <Menu placement="top-end">
            <MenuButton
              as={IconButton}
              aria-label="Account and logout"
              icon={<RiSettings4Line size={20} />}
              variant="ghost"
              color={C.cream}
              size="sm"
              _hover={{ bg: "rgba(255,255,255,0.12)" }}
            />
            <MenuList {...accountMenuListProps}>
              <MenuItem
                {...accountMenuItemProps}
                icon={<RiLogoutBoxRLine size={18} />}
                onClick={handleLogout}
              >
                Log out
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Box>
    </>
  );

  return (
    <StudentWrapper roles={["Student"]}>
      <Box minH="100vh" bg="#F3ECE6" dir="ltr" position="relative" w="100%" overflowX="hidden">
        {/* Desktop: fixed sidebar (out of document flow for width) */}
        <Flex
          display={{ base: "none", md: "flex" }}
          position="fixed"
          left={0}
          top={0}
          zIndex={30}
          w="260px"
          h="100vh"
          bg={C.sidebar}
          direction="column"
          borderRightWidth="1px"
          borderColor={C.border}
        >
          {SidebarNav}
        </Flex>

        {/* Mobile: single full-width row — do NOT sit beside main in a horizontal flex */}
        <Flex
          display={{ base: "flex", md: "none" }}
          position="sticky"
          top={0}
          zIndex={40}
          w="100%"
          maxW="100vw"
          bg={C.sidebar}
          px={3}
          py={2.5}
          align="center"
          justify="space-between"
          gap={2}
          borderBottomWidth="1px"
          borderColor={C.border}
          flexShrink={0}
        >
          <Text fontWeight="800" color="white" fontSize="sm" noOfLines={1} flexShrink={0}>
            Edutopia
          </Text>
          <Flex align="center" gap={2} flex="1" justify="flex-end" minW={0}>
            <Text
              as={Link}
              to="/students"
              fontSize="xs"
              fontWeight={isHome ? "800" : "600"}
              color={C.cream}
              whiteSpace="nowrap"
            >
              Home
            </Text>
            <Text
              as={Link}
              to="/students/courses"
              fontSize="xs"
              fontWeight={isCourses ? "800" : "600"}
              color={C.cream}
              whiteSpace="nowrap"
            >
              Courses
            </Text>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Settings"
                icon={<RiSettings4Line />}
                variant="ghost"
                color={C.cream}
                size="sm"
                flexShrink={0}
              />
              <MenuList {...accountMenuListProps}>
                <MenuItem
                  {...accountMenuItemProps}
                  icon={<RiLogoutBoxRLine size={18} />}
                  onClick={handleLogout}
                >
                  Log out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        {/* Main: full width on mobile; offset only on md+ */}
        <Box
          w="100%"
          maxW="100%"
          minW={0}
          ml={{ base: 0, md: "260px" }}
          minH="100vh"
        >
          {children}
        </Box>
      </Box>
    </StudentWrapper>
  );
};

export default StudentPortalShell;
